import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import AppInfo from '../components/AppInfo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  RadarIcon, 
  FileSpreadsheet, 
  Upload, 
  DollarSign, 
  AlertTriangle, 
  CheckCircle, 
  HelpCircle,
  TrendingUp,
  FileText,
  Calendar,
  Package,
  Globe,
  Download,
  RefreshCw,
  Info,
  Clock,
  BarChart3,
  ChevronRight,
  ExternalLink,
  X,
  Plus,
  Search,
  Filter,
  FileDown,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { generateRefundRadarPdf } from '../utils/pdfGenerator';
import { apiGet, apiPost, apiFetch } from '../lib/apiClient';

// Country options
const COUNTRIES = [
  'United States', 'China', 'Canada', 'Mexico', 'Vietnam', 'Germany', 'Japan',
  'South Korea', 'Taiwan', 'India', 'Thailand', 'Malaysia', 'Indonesia',
  'Philippines', 'Brazil', 'United Kingdom', 'France', 'Italy', 'Spain'
].sort();

const RefundRadar = ({ user, setUser }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [rulePacks, setRulePacks] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Quick scan form state
  const [quickForm, setQuickForm] = useState({
    import_date_start: new Date().toISOString().split('T')[0],
    import_date_end: '',
    hs_code: '',
    customs_value: '',
    duty_paid: '',
    country_of_origin: ''
  });
  
  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  
  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('decoded_sidebar_collapsed') === 'true';
  });

  useEffect(() => {
    const handleSidebarToggle = (e) => {
      setSidebarCollapsed(e.detail.collapsed);
    };
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const data = await apiGet('/api/refund-radar/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchScanHistory = useCallback(async () => {
    try {
      const data = await apiGet('/api/refund-radar/scans');
      setScanHistory(data.scans || []);
    } catch (err) {
      console.error('Failed to fetch scan history:', err);
    }
  }, []);

  const fetchRulePacks = useCallback(async () => {
    try {
      const data = await apiGet('/api/refund-radar/rule-packs');
      setRulePacks(data.rule_packs || []);
    } catch (err) {
      console.error('Failed to fetch rule packs:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchScanHistory();
    fetchRulePacks();
  }, [fetchDashboard, fetchScanHistory, fetchRulePacks]);

  const fetchScanDetails = async (runId) => {
    try {
      setLoading(true);
      const data = await apiGet(`/api/refund-radar/scans/${runId}`);
      setSelectedScan(data);
      setActiveTab('results');
    } catch (err) {
      toast.error('Failed to fetch scan details');
    } finally {
      setLoading(false);
    }
  };

  // Quick scan handler
  const handleQuickScan = async (e) => {
    e.preventDefault();
    
    if (!quickForm.import_date_start) {
      toast.error('Import date is required');
      return;
    }
    
    if (!quickForm.customs_value || !quickForm.duty_paid) {
      toast.error('Customs value and duty paid are required');
      return;
    }
    
    setLoading(true);
    try {
      const data = await apiPost('/api/refund-radar/scan/quick', {
        ...quickForm,
        customs_value: parseFloat(quickForm.customs_value) || 0,
        duty_paid: parseFloat(quickForm.duty_paid) || 0
      });
      toast.success('Scan completed successfully');
      setSelectedScan(data);
      setActiveTab('results');
      fetchDashboard();
      fetchScanHistory();
      // Reset form
      setQuickForm({
        import_date_start: new Date().toISOString().split('T')[0],
        import_date_end: '',
        hs_code: '',
        customs_value: '',
        duty_paid: '',
        country_of_origin: ''
      });
    } catch (err) {
      toast.error(err?.message || 'Failed to run scan');
    } finally {
      setLoading(false);
    }
  };

  // CSV upload handler
  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const data = await apiPost('/api/refund-radar/scan/csv', formData);
      toast.success(`Scan completed: ${data.summary?.affected_count || 0} affected entries found`);
      setSelectedScan(data);
      setActiveTab('results');
      fetchDashboard();
      fetchScanHistory();
      setCsvFile(null);
      setCsvPreview(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to process CSV');
    } finally {
      setLoading(false);
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const res = await apiFetch('/api/refund-radar/csv-template', { raw: true, auth: false });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'refund_radar_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Export results to CSV
  const exportResults = () => {
    if (!selectedScan?.results) return;
    
    const headers = ['Entry ID', 'Import Date', 'HS Code', 'Customs Value', 'Duty Paid', 'Status', 'Confidence', 'Refund Estimate', 'Eligibility Factor', 'Notes'];
    const rows = selectedScan.results.map(r => [
      r.entry_number || r.entry_id,
      r.import_date,
      r.hs_code || '',
      r.customs_value,
      r.duty_paid,
      r.status,
      r.confidence,
      r.refund_estimate,
      r.eligibility_factor,
      `"${r.notes?.replace(/"/g, '""') || ''}"`
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `refund_scan_${selectedScan.run_id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Results exported');
  };

  // Export to PDF
  const exportPdf = async () => {
    if (!selectedScan) return;
    try {
      const pdf = await generateRefundRadarPdf(selectedScan);
      pdf.save(`refund_radar_${selectedScan.run_id}.pdf`);
      toast.success('PDF exported');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  // Share scan results
  const shareScan = async () => {
    if (!selectedScan) return;
    try {
      const data = await apiPost(`/api/refund/scans/${selectedScan.run_id}/share`);
      const shareUrl = `${window.location.origin}${data.share_url}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard');
    } catch (e) {
      toast.error('Failed to create share link');
    }
  };

  // Toggle row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Status colors
  const getStatusConfig = (status) => {
    switch (status) {
      case 'affected':
        return { color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle, label: 'Affected' };
      case 'not_affected':
        return { color: 'text-slate-500', bg: 'bg-slate-50', icon: X, label: 'Not Affected' };
      case 'needs_review':
        return { color: 'text-amber-600', bg: 'bg-amber-50', icon: HelpCircle, label: 'Needs Review' };
      default:
        return { color: 'text-slate-500', bg: 'bg-slate-50', icon: HelpCircle, label: status };
    }
  };

  const getConfidenceConfig = (confidence) => {
    switch (confidence) {
      case 'high':
        return { color: 'text-emerald-600', bg: 'bg-emerald-50' };
      case 'medium':
        return { color: 'text-amber-600', bg: 'bg-amber-50' };
      case 'low':
        return { color: 'text-red-600', bg: 'bg-red-50' };
      default:
        return { color: 'text-slate-500', bg: 'bg-slate-50' };
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Dashboard charts data
  const confidenceChartData = dashboard?.confidence_distribution ? [
    { name: 'High', value: dashboard.confidence_distribution.high || 0, color: '#10B981' },
    { name: 'Medium', value: dashboard.confidence_distribution.medium || 0, color: '#F59E0B' },
    { name: 'Low', value: dashboard.confidence_distribution.low || 0, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  // Results chart data
  const resultsChartData = selectedScan?.summary ? [
    { name: 'Affected', value: selectedScan.summary.affected_count || 0, fill: '#10B981' },
    { name: 'Not Affected', value: selectedScan.summary.not_affected_count || 0, fill: '#64748B' },
    { name: 'Needs Review', value: selectedScan.summary.needs_review_count || 0, fill: '#F59E0B' }
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Sidebar user={user} setUser={setUser} activePage="refund-radar" />
      
      <main className={`pt-20 lg:pt-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <RadarIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div>
                      <h1 className="text-2xl font-bold text-slate-900 font-outfit">Tariff Refund Radar</h1>
                      <p className="text-slate-500 text-sm">Estimate potential refund exposure on import duties</p>
                    </div>
                    <AppInfo appId="refund-radar" className="ml-1" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { fetchDashboard(); fetchScanHistory(); }}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 text-sm font-medium">Legal Disclaimer</p>
                <p className="text-amber-700 text-xs mt-1">
                  This tool provides estimates for informational purposes only and does not constitute legal or financial advice. 
                  Actual refund eligibility depends on specific circumstances, documentation, and regulatory requirements. 
                  Consult with a licensed customs broker or trade attorney before pursuing any refund claims.
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-4 lg:w-[600px] bg-white border shadow-sm">
              <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger value="quick-scan" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Quick Scan</span>
              </TabsTrigger>
              <TabsTrigger value="csv-upload" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">CSV Upload</span>
              </TabsTrigger>
              <TabsTrigger value="results" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Results</span>
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-5 border shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-sm font-medium">Est. Refund Exposure</span>
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard?.total_refund_exposure)}</p>
                  <p className="text-xs text-slate-400 mt-1">Based on all scans</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-sm font-medium">Affected Entries</span>
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.total_affected_entries || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Of {dashboard?.total_entries_scanned || 0} total entries</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-sm font-medium">Needs Review</span>
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.total_needs_review || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Entries requiring review</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-sm font-medium">Doc Readiness</span>
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.documentation_readiness || 0}%</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Urgency: <span className={`font-medium ${dashboard?.urgency === 'high' ? 'text-red-600' : dashboard?.urgency === 'medium' ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {dashboard?.urgency?.toUpperCase() || 'LOW'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Confidence Distribution */}
                <div className="bg-white rounded-2xl p-6 border shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-4">Confidence Distribution</h3>
                  {confidenceChartData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={confidenceChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {confidenceChartData.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-400">
                      <p>No data available. Run a scan to see results.</p>
                    </div>
                  )}
                </div>

                {/* Recent Scans */}
                <div className="bg-white rounded-2xl p-6 border shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-4">Recent Scans</h3>
                  {dashboard?.recent_scans?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_scans.map((scan) => (
                        <div
                          key={scan.run_id}
                          onClick={() => fetchScanDetails(scan.run_id)}
                          className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              scan.ingestion_type === 'csv_upload' ? 'bg-blue-100' : 'bg-emerald-100'
                            }`}>
                              {scan.ingestion_type === 'csv_upload' ? (
                                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Search className="w-4 h-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {scan.ingestion_type === 'csv_upload' ? 'CSV Upload' : 'Quick Scan'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(scan.created_at).toLocaleDateString()} - {scan.entries_detected} entries
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-emerald-600">
                              {formatCurrency(scan.total_refund_estimate)}
                            </p>
                            <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <RadarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No scans yet. Start a scan to see results here.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Active Rule Packs */}
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Active Rule Packs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {rulePacks.filter(r => r.is_active !== false).map((rule) => (
                    <div key={rule.id} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{rule.program_name}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{rule.description}</p>
                          {rule.is_starter && (
                            <span className="inline-flex items-center mt-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Starter Rule
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Quick Scan Tab */}
            <TabsContent value="quick-scan" className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Quick Scan - Manual Entry</h3>
                <p className="text-slate-500 text-sm mb-6">
                  Enter import entry details to check for potential refund eligibility against active rule packs.
                </p>
                
                <form onSubmit={handleQuickScan} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="import_date" className="text-sm font-medium">Import Date *</Label>
                      <Input
                        id="import_date"
                        type="date"
                        value={quickForm.import_date_start}
                        onChange={(e) => setQuickForm({ ...quickForm, import_date_start: e.target.value })}
                        required
                        data-testid="quick-scan-date"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="hs_code" className="text-sm font-medium">HS Code</Label>
                      <Input
                        id="hs_code"
                        placeholder="e.g., 8471300000"
                        value={quickForm.hs_code}
                        onChange={(e) => setQuickForm({ ...quickForm, hs_code: e.target.value })}
                        data-testid="quick-scan-hs"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="country_of_origin" className="text-sm font-medium">Country of Origin</Label>
                      <Select
                        value={quickForm.country_of_origin}
                        onValueChange={(val) => setQuickForm({ ...quickForm, country_of_origin: val })}
                      >
                        <SelectTrigger data-testid="quick-scan-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="customs_value" className="text-sm font-medium">Customs Value (USD) *</Label>
                      <Input
                        id="customs_value"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g., 50000"
                        value={quickForm.customs_value}
                        onChange={(e) => setQuickForm({ ...quickForm, customs_value: e.target.value })}
                        required
                        data-testid="quick-scan-value"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="duty_paid" className="text-sm font-medium">Duty Paid (USD) *</Label>
                      <Input
                        id="duty_paid"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g., 12500"
                        value={quickForm.duty_paid}
                        onChange={(e) => setQuickForm({ ...quickForm, duty_paid: e.target.value })}
                        required
                        data-testid="quick-scan-duty"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                      data-testid="quick-scan-submit"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          Run Quick Scan
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>

            {/* CSV Upload Tab */}
            <TabsContent value="csv-upload" className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-900">CSV Upload</h3>
                    <p className="text-slate-500 text-sm">Upload a CSV file with multiple import entries for batch analysis.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                </div>
                
                {/* Upload Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    csvFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCsvFile(file);
                        setCsvPreview({ name: file.name, size: file.size });
                      }
                    }}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  
                  {csvFile ? (
                    <div className="flex items-center justify-center gap-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{csvPreview?.name}</p>
                        <p className="text-sm text-slate-500">{(csvPreview?.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCsvFile(null);
                          setCsvPreview(null);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 font-medium">Click to upload CSV file</p>
                      <p className="text-slate-400 text-sm mt-1">or drag and drop</p>
                    </>
                  )}
                </div>
                
                {/* Required Columns Info */}
                <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-900 text-sm mb-2">Required Columns</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-emerald-600" />
                      <span>import_date</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3 text-emerald-600" />
                      <span>customs_value</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3 text-emerald-600" />
                      <span>duty_paid</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-400">hs_code (opt)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Globe className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-400">country_of_origin (opt)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-400">entry_number (opt)</span>
                    </div>
                  </div>
                </div>
                
                {/* Upload Button */}
                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleCsvUpload}
                    disabled={!csvFile || loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    data-testid="csv-upload-submit"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload & Analyze
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results" className="space-y-6">
              {selectedScan ? (
                <>
                  {/* Savings Overview Hero */}
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div>
                        <h2 className="text-lg font-medium opacity-90 mb-1">Potential Refund Opportunity</h2>
                        <p className="text-4xl font-bold mb-2">
                          {formatCurrency(selectedScan.summary?.total_refund_estimate)}
                        </p>
                        <p className="text-sm opacity-80">
                          Based on {selectedScan.summary?.affected_count || 0} affected {(selectedScan.summary?.affected_count || 0) === 1 ? 'entry' : 'entries'} from Trump-era tariffs
                        </p>
                      </div>
                      <div className="flex gap-6 lg:gap-8">
                        <div className="text-center">
                          <p className="text-sm opacity-80 mb-1">Original Duty Paid</p>
                          <p className="text-2xl font-bold">
                            {formatCurrency(selectedScan.results?.reduce((sum, r) => sum + (r.duty_paid || 0), 0))}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm opacity-80 mb-1">Potential Recovery</p>
                          <p className="text-2xl font-bold">
                            {((selectedScan.summary?.total_refund_estimate / (selectedScan.results?.reduce((sum, r) => sum + (r.duty_paid || 0), 0) || 1)) * 100).toFixed(0)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl p-5 border shadow-sm">
                      <p className="text-slate-500 text-sm mb-1">Total Refund Estimate</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(selectedScan.summary?.total_refund_estimate)}
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border shadow-sm">
                      <p className="text-slate-500 text-sm mb-1">Entries Analyzed</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {selectedScan.summary?.total_entries || selectedScan.entries_detected || 0}
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border shadow-sm">
                      <p className="text-slate-500 text-sm mb-1">Affected</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {selectedScan.summary?.affected_count || selectedScan.entries_affected || 0}
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl p-5 border shadow-sm">
                      <p className="text-slate-500 text-sm mb-1">Doc Readiness</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {selectedScan.summary?.documentation_readiness?.toFixed(0) || 0}%
                      </p>
                    </div>
                  </div>

                  {/* Dual Charts: Duty vs Refund Comparison + Breakdown by Program */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Original Duty vs Potential Refund */}
                    <div className="bg-white rounded-2xl p-6 border shadow-sm">
                      <h3 className="font-semibold text-slate-900 mb-4">Original Duty vs Potential Refund</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={[
                              { 
                                name: 'Comparison', 
                                'Original Duty': selectedScan.results?.reduce((sum, r) => sum + (r.duty_paid || 0), 0) || 0,
                                'Potential Refund': selectedScan.summary?.total_refund_estimate || 0
                              }
                            ]}
                            layout="vertical"
                            margin={{ left: 20, right: 30 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                            <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" hide />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="Original Duty" fill="#94A3B8" radius={[0, 4, 4, 0]} barSize={40} />
                            <Bar dataKey="Potential Refund" fill="#10B981" radius={[0, 4, 4, 0]} barSize={40} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-emerald-800">
                          <strong>
                            {((selectedScan.summary?.total_refund_estimate / (selectedScan.results?.reduce((sum, r) => sum + (r.duty_paid || 0), 0) || 1)) * 100).toFixed(0)}%
                          </strong> of original duty may be recoverable due to tariff rulings
                        </p>
                      </div>
                    </div>

                    {/* Breakdown by Tariff Program */}
                    <div className="bg-white rounded-2xl p-6 border shadow-sm">
                      <h3 className="font-semibold text-slate-900 mb-4">Exposure by Tariff Program</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={(() => {
                                const programTotals = {};
                                selectedScan.results?.forEach(r => {
                                  if (r.status === 'affected' && r.matched_rule_packs?.length > 0) {
                                    const refundPerRule = (r.refund_estimate || 0) / r.matched_rule_packs.length;
                                    r.matched_rule_packs.forEach(ruleId => {
                                      const rule = rulePacks.find(rp => rp.id === ruleId);
                                      const name = rule?.program_name || 'Unknown Program';
                                      programTotals[name] = (programTotals[name] || 0) + refundPerRule;
                                    });
                                  }
                                });
                                const colors = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899'];
                                return Object.entries(programTotals).map(([name, value], i) => ({
                                  name: name.replace('Tariffs', '').replace('Section', 'Sec.').trim(),
                                  value,
                                  fill: colors[i % colors.length]
                                }));
                              })()}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            >
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Entry Status Distribution */}
                  {resultsChartData.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 border shadow-sm">
                      <h3 className="font-semibold text-slate-900 mb-4">Entry Status Distribution</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={resultsChartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Results Table */}
                  <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">Entry Details</h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportResults}
                          className="gap-2"
                          data-testid="export-results-btn"
                        >
                          <FileDown className="w-4 h-4" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportPdf}
                          className="gap-2"
                          data-testid="export-pdf-btn"
                        >
                          <FileText className="w-4 h-4" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={shareScan}
                          className="gap-2"
                          data-testid="share-scan-btn"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Share
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Entry/ID</TableHead>
                            <TableHead>Import Date</TableHead>
                            <TableHead>HS Code</TableHead>
                            <TableHead className="text-right">Customs Value</TableHead>
                            <TableHead className="text-right">Duty Paid</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Confidence</TableHead>
                            <TableHead className="text-right">Refund Est.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedScan.results?.map((result) => {
                            const statusConfig = getStatusConfig(result.status);
                            const confConfig = getConfidenceConfig(result.confidence);
                            const isExpanded = expandedRows[result.entry_id];
                            const StatusIcon = statusConfig.icon;
                            
                            return (
                              <React.Fragment key={result.entry_id}>
                                <TableRow
                                  className="cursor-pointer hover:bg-slate-50"
                                  onClick={() => toggleRow(result.entry_id)}
                                >
                                  <TableCell>
                                    {isExpanded ? (
                                      <ChevronUp className="w-4 h-4 text-slate-400" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-slate-400" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {result.entry_number || result.entry_id.substring(0, 8)}
                                  </TableCell>
                                  <TableCell>{result.import_date}</TableCell>
                                  <TableCell className="font-mono text-sm">{result.hs_code || '-'}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(result.customs_value)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(result.duty_paid)}</TableCell>
                                  <TableCell>
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                                      <StatusIcon className="w-3 h-3" />
                                      {statusConfig.label}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${confConfig.bg} ${confConfig.color}`}>
                                      {result.confidence?.toUpperCase()}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-emerald-600">
                                    {formatCurrency(result.refund_estimate)}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-slate-50">
                                    <TableCell colSpan={9} className="py-4">
                                      <div className="pl-10 space-y-3">
                                        <div>
                                          <p className="text-xs font-medium text-slate-500 mb-1">Notes</p>
                                          <p className="text-sm text-slate-700">{result.notes}</p>
                                        </div>
                                        <div className="flex gap-6">
                                          <div>
                                            <p className="text-xs font-medium text-slate-500 mb-1">Eligibility Factor</p>
                                            <p className="text-sm font-semibold">{(result.eligibility_factor * 100).toFixed(0)}%</p>
                                          </div>
                                          <div>
                                            <p className="text-xs font-medium text-slate-500 mb-1">Matched Rules</p>
                                            <div className="flex flex-wrap gap-1">
                                              {result.matched_rule_packs?.length > 0 ? (
                                                result.matched_rule_packs.map((ruleId) => {
                                                  const rule = rulePacks.find(r => r.id === ruleId);
                                                  return (
                                                    <span key={ruleId} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                      {rule?.program_name || ruleId.substring(0, 12)}
                                                    </span>
                                                  );
                                                })
                                              ) : (
                                                <span className="text-xs text-slate-400">None</span>
                                              )}
                                            </div>
                                          </div>
                                          {result.missing_fields?.length > 0 && (
                                            <div>
                                              <p className="text-xs font-medium text-slate-500 mb-1">Missing Fields</p>
                                              <p className="text-xs text-amber-600">{result.missing_fields.join(', ')}</p>
                                            </div>
                                          )}
                                        </div>
                                        <div className="pt-2 border-t border-slate-200 mt-2">
                                          <a
                                            href={`/analyzer?description=${encodeURIComponent(result.description || result.product_name || '')}&hs_code=${encodeURIComponent(result.hs_code || '')}`}
                                            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                            data-testid={`reclassify-${result.entry_id}`}
                                          >
                                            <RefreshCw className="w-3 h-3" /> Re-classify this product
                                          </a>
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-2xl p-12 border shadow-sm text-center">
                  <RadarIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Results Yet</h3>
                  <p className="text-slate-500 mb-6">Run a quick scan or upload a CSV to see results here.</p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab('quick-scan')}
                      className="gap-2"
                    >
                      <Search className="w-4 h-4" />
                      Quick Scan
                    </Button>
                    <Button
                      onClick={() => setActiveTab('csv-upload')}
                      className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Upload className="w-4 h-4" />
                      CSV Upload
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Scan History */}
          {scanHistory.length > 0 && activeTab === 'dashboard' && (
            <div className="mt-8 bg-white rounded-2xl p-6 border shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Scan History</h3>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Entries</TableHead>
                      <TableHead className="text-right">Affected</TableHead>
                      <TableHead className="text-right">Refund Est.</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scanHistory.map((scan) => (
                      <TableRow key={scan.run_id} className="hover:bg-slate-50">
                        <TableCell>{new Date(scan.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            scan.ingestion_type === 'csv_upload' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {scan.ingestion_type === 'csv_upload' ? (
                              <><FileSpreadsheet className="w-3 h-3" /> CSV</>
                            ) : (
                              <><Search className="w-3 h-3" /> Quick</>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{scan.entries_detected}</TableCell>
                        <TableCell className="text-right">{scan.entries_affected}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {formatCurrency(scan.total_refund_estimate)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchScanDetails(scan.run_id)}
                            className="gap-1"
                          >
                            View <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RefundRadar;
